import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import GenderBadge from "../components/GenderBadge";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { can } from "../services/permissions";
import { fetchBusinessProfile } from "../services/lookupService";

export default function BusinessProfile() {
  const { t, i18n } = useTranslation();
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const canCreateBranch = can(getCurrentUser(), "branches.create");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await fetchBusinessProfile();
        if (!cancelled) setBusiness(data);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err, t("business.loadFailed")));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language, t]);

  if (loading) {
    return <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">{business.name}</h1>
          <p className="text-sm text-ink-2">{t("business.subtitle")}</p>
        </div>
        {/* Shown only to a role that may actually open a branch. */}
        {canCreateBranch && (
          <Link to="/branches/create" className="btn-primary">
            {t("branches.new")}
          </Link>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-5">
        {/* Profile */}
        <div className="card p-5 lg:col-span-2">
          <div className="mb-4 flex items-center gap-3">
            {business.logo ? (
              <img src={business.logo} alt="" className="h-14 w-14 rounded-xl object-cover" />
            ) : (
              <div className="grid h-14 w-14 place-items-center rounded-xl bg-accent text-card-title font-bold text-accent-on">
                {business.name?.[0]?.toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-semibold">{business.name}</p>
              <span
                className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${
                  business.is_active
                    ? "bg-accent-wash text-accent-text"
                    : "bg-raised text-ink-2"
                }`}
              >
                {business.is_active ? t("business.active") : t("business.inactive")}
              </span>
            </div>
          </div>

          <dl className="space-y-2 text-sm">
            <Row label={t("business.country")}>
              {business.country ? `${business.country.flag ?? ""} ${business.country.name}` : "—"}
            </Row>
            <Row label={t("business.timezone")}>{business.country?.timezone ?? "—"}</Row>
            {/* Read-only: only the platform admin sets who a business serves. */}
            <Row label={t("business.servedGender")}>
              <GenderBadge gender={business.served_gender} />
              <p className="mt-1 text-xs text-ink-2">{t("business.servedGenderHint")}</p>
            </Row>
            <Row label={t("business.owner")}>
              {business.owner ? (
                <>
                  {business.owner.name}
                  <br />
                  <span className="text-ink-2">{business.owner.email}</span>
                </>
              ) : (
                "—"
              )}
            </Row>
            <Row label={t("business.branches")}>{business.branches?.length ?? 0}</Row>
          </dl>
        </div>

        {/* Business types */}
        <div className="card p-5 lg:col-span-3">
          <h2 className="mb-3 text-sm font-semibold">
            {t("business.businessTypes")}
            <span className="ms-2 rounded-full bg-raised px-2 py-1 text-xs text-ink-2">
              {business.business_types?.length ?? 0}
            </span>
          </h2>

          {business.business_types?.length ? (
            <div className="flex flex-wrap gap-2">
              {business.business_types.map((type) => (
                <span
                  key={type.id}
                  className="rounded-full bg-accent-wash px-3 py-1 text-xs font-medium text-accent-text"
                >
                  {type.name}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-ink-2">{t("business.noBusinessTypes")}</p>
          )}
        </div>

        {/* Branches - hidden outright when there is nothing to list and no way to add one. */}
        {(business.branches?.length || canCreateBranch) && (
          <div className="card overflow-hidden lg:col-span-5">
            <div className="border-b border-line px-4 py-3">
              <h2 className="text-sm font-semibold">
                {t("business.branches")}
                <span className="ms-2 rounded-full bg-raised px-2 py-1 text-xs text-ink-2">
                  {business.branches?.length ?? 0}
                </span>
              </h2>
            </div>
  
            {business.branches?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-start text-sm">
                  <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-ink-2">
                    <tr>
                      <th className="px-4 py-3">{t("branches.table.branch")}</th>
                      <th className="px-4 py-3">{t("branches.table.city")}</th>
                      <th className="px-4 py-3">{t("branches.table.gender")}</th>
                      <th className="px-4 py-3">{t("branches.table.businessTypes")}</th>
                      <th className="px-4 py-3">{t("branches.table.currency")}</th>
                      <th className="px-4 py-3">{t("branches.table.media")}</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {business.branches.map((branch) => (
                      <tr key={branch.id} className="border-b border-line last:border-0">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {branch.logo && (
                              <img src={branch.logo} alt="" className="h-8 w-8 rounded-lg object-cover" />
                            )}
                            <div>
                              <p className="font-medium">{branch.name}</p>
                              <p className="text-xs text-ink-2">{branch.address}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-ink-2">{branch.city?.name ?? "—"}</td>
                        <td className="px-4 py-3">
                          <GenderBadge gender={branch.gender} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(branch.business_types ?? []).map((type) => (
                              <span
                                key={type.id}
                                className="rounded-full bg-raised px-2 py-1 text-xs text-ink"
                              >
                                {type.name}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-ink-2">{branch.currency?.code ?? "—"}</td>
                        <td className="px-4 py-3 text-ink-2">{branch.media?.length ?? 0}</td>
                        <td className="px-4 py-3 text-end">
                          <Link
                            to={`/branches/${branch.id}`}
                            className="text-sm font-medium text-accent-text hover:underline"
                          >
                            {t("common.view")}
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-4 py-12 text-center">
                <p className="text-sm text-ink-2">{t("business.noBranches")}</p>
                <Link to="/branches/create" className="btn-primary mt-3 inline-block">
                  {t("branches.createFirst")}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex gap-3">
      <dt className="w-28 flex-shrink-0 text-ink-2">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
    </div>
  );
}
